import { Minus, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import useAddCartItem from '~/App/Cart/hooks/useAddCartItem'
import usePublishCart from '~/App/Cart/hooks/usePublishCart'
import type { ChatThreadEvent, ProductsAdditionalFields } from '~/App/ChatThreads/@types'
import { IncomingImageMessage } from '~/App/components/messages/IncomingImageMessage'
import { MessageTextParagraph } from '~/App/components/messages/MessageText'
import { Button } from '~/DesignSystem/components/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '~/DesignSystem/components/input-group'
import { toast } from '~/DesignSystem/components/sonner'

import { MAX_QUANTITY, MIN_QUANTITY } from './constants'
import { sanitizeQuantity } from './utils'

type IncommingProductMessageProps = {
  event: ChatThreadEvent
  data: NonNullable<ProductsAdditionalFields['product_items']>[number]
}

const DEFAULT_QUANTITY = 1

function IncommingProductMessage({ event, data }: IncommingProductMessageProps) {
  const intl = useIntl()
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const description = data.description?.split('/n').join(' ')
  const price = data.sale_price_in_cents || data.price_in_cents
  const images = [data.image_url, ...(data.images?.filter(image => image !== data.image_url) || [])]

  const {
    mutate: addCartItem,
    isPending: addingCartItem,
    isSuccess: addCartItemSuccess,
    error: addCartItemError,
  } = useAddCartItem()

  const handleAddToCart = () => {
    addCartItem({
      variant_id: data.id,
      quantity,
    })
  }

  const {
    mutate: publishCart,
    isPending: publishingCart,
    isSuccess: publishCartSuccess,
    error: publishCartError,
  } = usePublishCart()

  const handlePublishCart = () => {
    publishCart({
      variant_id: data.id,
      quantity,
    })
  }

  useEffect(() => {
    if (addCartItemSuccess || publishCartSuccess) {
      setQuantity(DEFAULT_QUANTITY)
    }
  }, [addCartItemSuccess, publishCartSuccess])

  useEffect(() => {
    if (addCartItemError || publishCartError) {
      toast.error(intl.formatMessage({ id: 'error.generic' }))
    }
  }, [addCartItemError, publishCartError])

  const disabled = addingCartItem || publishingCart

  return (
    <IncomingImageMessage
      src={images}
      title={data.name}
      time={event.attributes.timestamp * 1000}
      variant="outline"
    >
      <p className="font-semibold my-4">{data.name}</p>
      <MessageTextParagraph>
        <span className={descriptionExpanded ? 'line-clamp-10' : 'line-clamp-2'}>
          {description}
        </span>
        <a
          className="font-semibold hover:text-primary"
          href={data.url}
          target="_parent"
          onClick={event => {
            if (!descriptionExpanded) {
              event.preventDefault()
              setDescriptionExpanded(true)
            }
          }}
        >
          <FormattedMessage id="productMessage.viewMore" />
        </a>
      </MessageTextParagraph>

      {!!price && (
        <>
          <p className="text-base font-semibold text-primary my-1">
            <FormattedNumber value={price / 100} currency={data.currency} style="currency" />
          </p>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <InputGroup className="w-auto h-10">
                <InputGroupAddon>
                  <InputGroupButton
                    size="icon-xs"
                    aria-label={intl.formatMessage({ id: 'productMessage.quantity.decrease' })}
                    disabled={quantity <= MIN_QUANTITY || disabled}
                    onClick={() => setQuantity(sanitizeQuantity(quantity - 1))}
                  >
                    <Minus />
                  </InputGroupButton>
                </InputGroupAddon>
                <InputGroupInput
                  type="number"
                  disabled={disabled}
                  className="text-center no-spinner h-10"
                  value={quantity}
                  min={MIN_QUANTITY}
                  max={MAX_QUANTITY}
                  onChange={event => setQuantity(sanitizeQuantity(Number(event.target.value)))}
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    size="icon-xs"
                    aria-label={intl.formatMessage({ id: 'productMessage.quantity.increase' })}
                    disabled={quantity >= MAX_QUANTITY || disabled}
                    onClick={() => setQuantity(sanitizeQuantity(quantity + 1))}
                  >
                    <Plus />
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                disabled={disabled}
                onClick={handleAddToCart}
              >
                <FormattedMessage id="productMessage.addToCart" />
              </Button>
            </div>
            <Button size="lg" className="w-full" onClick={handlePublishCart} disabled={disabled}>
              <FormattedMessage id="productMessage.buyNow" />
            </Button>
          </div>
        </>
      )}
    </IncomingImageMessage>
  )
}

export default IncommingProductMessage
